import React, { useState, useRef, useEffect } from 'react';
import type { FC } from 'react';

import type { TemplateTag } from '@/api/types/tag';
import { timeAgo } from '@/utils';

import { BrokenThumbnailIcon } from './broken-thumbnail-icon';
import { Chip } from './chip';
import { Menu, type MenuItem } from './menu';

interface TemplateTileProps {
    title: string;
    createdAt: string;
    hideCreatedAt?: boolean;
    showFrameCount?: boolean;
    frameCount?: number;
    tags: TemplateTag[];
    isDraft?: boolean;
    thumbnail: string;
    menuItems?: MenuItem[];
    isRenaming?: boolean;
    onRename?: (title: string) => void;
    onClick?: () => void;
}

export const TemplateTile: FC<TemplateTileProps> = ({
    title,
    createdAt,
    hideCreatedAt,
    showFrameCount,
    frameCount,
    tags,
    thumbnail,
    menuItems,
    isDraft,
    isRenaming,
    onRename,
    onClick,
}) => {
    const displayedTags = tags.slice(0, 3);
    const remainingTags = tags.slice(3);
    const remainingTagsCount = remainingTags.length;
    const [isRemainingTagsVisible, setRemainingTagsVisible] = useState(false);
    const remainingTagsRef = useRef<HTMLSpanElement | null>(null);
    const [titleValue, setTitleValue] = useState(title);
    const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);

    useEffect(() => {
        if (!isRenaming) {
            setTitleValue(title);
        }
    }, [isRenaming, title]);

    return (
        <div
            className="flex flex-col w-full border-gray-130 border-[1px] border-solid rounded-lg relative"
            onClick={onClick}
            onKeyUp={isRenaming ? () => {} : (e) => e.key === 'Enter' && onClick && onClick()}
        >
            <div className="chess p-2">
                {thumbnailLoadFailed || !thumbnail ? (
                    <div className="w-full aspect-video flex items-center justify-center">
                        <BrokenThumbnailIcon className="w-2/3 h-2/3" />
                    </div>
                ) : (
                    <img
                        src={thumbnail}
                        alt="thumbnail"
                        className="w-full aspect-video object-scale-down"
                        onError={(e) => {
                            setThumbnailLoadFailed(true);
                        }}
                    />
                )}
            </div>
            <div className="pl-4 py-2 w-full flex flex-col">
                <div className="flex flex-row justify-between items-center h-11">
                    <div className="flex flex-row gap-2 items-center max-w-[90%]">
                        {!isDraft && (
                            <div className="flex flex-row gap-2 overflow-y-scroll no-scrollbar items-center">
                                {displayedTags.map((tag) => (
                                    <Chip key={tag.tag.id} title={tag.tag.name} />
                                ))}
                            </div>
                        )}
                        {!isDraft && remainingTagsCount > 0 && (
                            <span
                                ref={remainingTagsRef}
                                className="text-xs text-gray-110 cursor-pointer relative"
                                onMouseEnter={() => setRemainingTagsVisible(true)}
                                onMouseLeave={() => setRemainingTagsVisible(false)}
                            >
                                +{remainingTagsCount}
                                {isRemainingTagsVisible && (
                                    <div className="flex flex-col gap-1 absolute left-0 mt-2 p-2 bg-gray-100 text-white text-xs rounded shadow-lg z-10">
                                        {remainingTags.map((tag) => (
                                            <div key={tag.tag.id}>{tag.tag.name}</div>
                                        ))}
                                    </div>
                                )}
                            </span>
                        )}
                        {isDraft && (
                            <div className="text-red-50 text-sm bg-red-05 bg-opacity-20 px-3 py-1 rounded ">Draft</div>
                        )}
                    </div>
                    {menuItems && <Menu items={menuItems} />}
                </div>
                {isRenaming ? (
                    <input
                        type="text"
                        className="mr-4 text-white bg-transparent border-none outline-none text-base truncate w-full overflow-hidden whitespace-nowrap"
                        onKeyUp={(e) => e.key === 'Enter' && onRename?.(titleValue)}
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        // biome-ignore lint/a11y/noAutofocus: <explanation>
                        autoFocus
                    />
                ) : (
                    <p className="text-white text-base truncate w-full overflow-hidden whitespace-nowrap pr-4">
                        {title}
                    </p>
                )}
                {!hideCreatedAt && (
                    <p className="flex flex-row gap-1 text-white text-xs w-full text-ellipsis">
                        <span className="text-gray-60">Created</span>
                        {`${timeAgo(createdAt)}`}
                    </p>
                )}
                {showFrameCount && (
                    <p className="text-gray-110 text-xs">
                        {frameCount} Frame{frameCount !== 1 ? 's' : ''}
                    </p>
                )}
            </div>
        </div>
    );
};
